import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Circle, Sparkles, PawPrint, Piano, EyeOff, Music, X, Timer } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { stopSpeaking } from "@/lib/buddy";
import { tone, NOTE } from "@/lib/babySound";
import { BubblePop } from "./BubblePop";
import { GlowTouch } from "./GlowTouch";
import { AnimalSounds } from "./AnimalSounds";
import { PianoDrums } from "./PianoDrums";
import { Peekaboo } from "./Peekaboo";
import { SingAlong } from "./SingAlong";

const GAMES = [
  { id: "bubbles", label: "Bubble Pop", Icon: Circle, color: "#6BB3E0", C: BubblePop },
  { id: "glow", label: "Glow Touch", Icon: Sparkles, color: "#A68BE0", C: GlowTouch },
  { id: "animals", label: "Animal Sounds", Icon: PawPrint, color: "#F2C94C", C: AnimalSounds },
  { id: "piano", label: "Piano & Drums", Icon: Piano, color: "#F28C38", C: PianoDrums },
  { id: "peekaboo", label: "Peekaboo", Icon: EyeOff, color: "#E58FB8", C: Peekaboo },
  { id: "songs", label: "Sing-along", Icon: Music, color: "#9BD08A", C: SingAlong },
];
const KEY = "cl_baby_play_minutes";

const HoldButton = ({ onDone, label, testId, className = "" }) => {
  const [p, setP] = useState(0);
  const t = useRef(null);
  const start = (e) => { e.stopPropagation(); const s = Date.now(); t.current = setInterval(() => { const v = Math.min(1, (Date.now() - s) / 1500); setP(v); if (v >= 1) { clearInterval(t.current); setP(0); onDone(); } }, 40); };
  const end = () => { clearInterval(t.current); setP(0); };
  return (
    <button onPointerDown={start} onPointerUp={end} onPointerLeave={end} onContextMenu={(e) => e.preventDefault()} data-testid={testId}
      className={`relative overflow-hidden select-none px-4 py-2 rounded-full bg-black/40 text-white text-xs backdrop-blur ${className}`}>
      <span className="absolute inset-y-0 left-0 bg-white/30" style={{ width: `${p * 100}%` }} /><span className="relative inline-flex items-center gap-1.5">{label}</span>
    </button>
  );
};

const Rest = ({ onMore }) => {
  useEffect(() => { tone(NOTE.G4, 1.2, { vol: 0.1 }); tone(NOTE.C5, 1.6, { vol: 0.08, at: 0.6 }); }, []);
  return (
    <div className="absolute inset-0 bg-[#1B2B3A] flex flex-col items-center justify-center text-center text-[#E8E4F5] px-6" data-testid="play-rest">
      <span className="w-40 h-40 rounded-full bg-[#A68BE0]/40 baby-breathe" />
      <p className="font-display text-4xl mt-10">All done! Time to cuddle.</p>
      <p className="mt-3 text-[#C9BFEA]">Breathe in… and out… together.</p>
      <HoldButton onDone={onMore} label="Hold for 2 more minutes" testId="play-more" className="mt-10" />
    </div>
  );
};

export const PlayMode = ({ onClose }) => {
  const { buddy } = useProfile();
  const [game, setGame] = useState(null);
  const [mins, setMins] = useState(() => Number(localStorage.getItem(KEY)) || 5);
  const [left, setLeft] = useState(mins * 60);
  const root = useRef(null);
  useEffect(() => { root.current?.requestFullscreen?.().catch(() => {}); return () => { stopSpeaking(); if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); }; }, []);
  useEffect(() => { if (left <= 0) return; const t = setInterval(() => setLeft((s) => s - 1), 1000); return () => clearInterval(t); }, [left > 0]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickMins = (m) => { setMins(m); setLeft(m * 60); localStorage.setItem(KEY, String(m)); };
  const G = GAMES.find((g) => g.id === game);
  return createPortal(
    <div ref={root} className="fixed inset-0 z-[95] bg-[#FDFBF7] select-none overscroll-none" data-testid="baby-play-mode" onContextMenu={(e) => e.preventDefault()}>
      {left <= 0 ? <Rest onMore={() => setLeft(120)} /> : G ? <G.C voice={buddy?.voice} /> : (
        <div className="absolute inset-0 overflow-y-auto pt-24 pb-10 px-5 sm:px-10 bg-[#FFF8EC]" data-testid="play-menu">
          <div className="flex flex-wrap items-end gap-4"><p className="font-display text-4xl text-[#2C4A3E] flex-1">Let's play!</p>
          <div className="flex flex-wrap items-center gap-2 text-sm text-stone-600" data-testid="play-timer-options">
            <Timer className="w-4 h-4" />Play time:
            {[3, 5, 10, 15].map((m) => <button key={m} onClick={() => pickMins(m)} data-testid={`play-mins-${m}`} className={`px-3 py-1.5 rounded-full ${mins === m ? "bg-[#2C4A3E] text-white" : "bg-white border border-[#E6DEC3]"}`}>{m} min</button>)}
          </div></div>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-4">
            {GAMES.map((g) => (
              <button key={g.id} onClick={() => setGame(g.id)} data-testid={`play-game-${g.id}`} className="rounded-[2rem] p-6 aspect-[4/3] lg:aspect-[16/9] flex flex-col items-center justify-center gap-3 hover:scale-[1.03] transition-transform" style={{ background: `${g.color}33`, border: `4px solid ${g.color}` }}>
                <g.Icon className="w-16 h-16" style={{ color: g.color }} strokeWidth={1.6} /><span className="font-display text-2xl text-[#2C4A3E]">{g.label}</span>
              </button>))}
          </div>
          <p className="mt-6 text-xs text-stone-500">Experts suggest short, shared screen time for under-2s. Play together!</p>
        </div>)}
      <div className="absolute top-4 inset-x-4 flex items-center gap-2 z-10">
        {G && left > 0 && <HoldButton onDone={() => { stopSpeaking(); setGame(null); }} label="Hold for games" testId="play-back" />}
        <span className="ml-auto px-3 py-2 rounded-full bg-black/40 text-white text-xs backdrop-blur tabular-nums" data-testid="play-time-left">{Math.max(0, Math.floor(left / 60))}:{String(Math.max(0, left % 60)).padStart(2, "0")}</span>
        <HoldButton onDone={onClose} label={<><X className="w-3.5 h-3.5" />Hold to exit</>} testId="play-exit" />
      </div>
    </div>, document.body);
};
